import { crashlytics, getApiKeys } from "./firebase";

export interface GeocodeResult {
  isValid: boolean;
  formattedAddress?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

const SCRIPT_ID = "google-maps-script";
let loadingPromise: Promise<void> | null = null;

/**
 * Parses a Google API error object and returns a user-friendly string.
 * @param errorData The error object from a failed Google API response.
 * @returns A user-friendly error message.
 */
const getGoogleApiErrorMessage = (errorData: any): string => {
  const message = errorData?.error?.message || "An unknown API error occurred.";
  if (/provided Place ID is no longer valid/i.test(message)) {
    return "This location is no longer valid. Please search and select the location again.";
  }
  if (message.includes("not enabled")) {
    return "A required Google Maps API is not enabled for your key. Please check your Google Cloud Console.";
  }
  if (/API key not valid/.test(message) || /Invalid Key/.test(message)) {
    return "The Google Maps API Key is invalid.";
  }
  if (/Billing required/.test(message)) {
    return "Billing is not enabled for the project. Please check your Google Cloud Console.";
  }
  return message;
};

const resetGoogleMapsApi = () => {
  const script = document.getElementById(SCRIPT_ID);
  if (script) {
    script.remove();
  }
  // Correctly reset the window properties to allow reloading.
  (window as any).google = undefined;
  (window as any).gm_authFailure = undefined;
  loadingPromise = null;
};

/**
 * Idempotent function to load the Google Maps JavaScript API script.
 * @param libraries An array of strings for the libraries to load, e.g., ['places'].
 * @returns A promise that resolves when the script is loaded.
 */
export const loadGoogleMapsApi = (
  libraries: string[] = ["places"],
): Promise<void> => {
  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = new Promise(async (resolve, reject) => {
    if ((window as any).google?.maps) {
      return resolve();
    }

    const { GOOGLE_API_KEY } = await getApiKeys();
    if (!GOOGLE_API_KEY) {
      loadingPromise = null;
      return reject(
        new Error(
          "Map features are unavailable. Please set the `API_KEY` environment variable.",
        ),
      );
    }

    (window as any).gm_authFailure = () => {
      resetGoogleMapsApi();
      reject(
        new Error(
          "Google Maps authentication failed. The API key is invalid or has insufficient permissions.",
        ),
      );
    };

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=${libraries.join(",")}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      delete (window as any).gm_authFailure;
      resolve();
    };
    script.onerror = (e) => {
      resetGoogleMapsApi();
      reject(
        new Error(
          "Failed to load Google Maps script. Check network connection or API key permissions.",
        ),
      );
    };
    document.head.appendChild(script);
  });

  return loadingPromise;
};

/**
 * Fetches place predictions from the Places API (New) REST endpoint.
 * @param input The user's input string.
 * @returns A promise that resolves with an array of autocomplete predictions.
 */
export const getPlaceAutocomplete = async (input: string): Promise<any[]> => {
  const { GOOGLE_API_KEY } = await getApiKeys();
  if (!GOOGLE_API_KEY) {
    throw new Error(
      "API Key not found. Please set the `API_KEY` environment variable.",
    );
  }
  if (!input || input.length < 3) {
    return [];
  }

  const url = "https://places.googleapis.com/v1/places:autocomplete";

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": GOOGLE_API_KEY,
      "X-Goog-FieldMask":
        "suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat",
    },
    body: JSON.stringify({
      input: input,
      locationBias: {
        rectangle: {
          low: { latitude: 1.15, longitude: 103.59 },
          high: { latitude: 1.47, longitude: 104.05 },
        },
      },
      includedRegionCodes: ["sg"],
      languageCode: "en",
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    crashlytics.logError(
      `Error fetching place autocomplete: ${JSON.stringify(errorData)}`,
      "locationService.ts",
      new Error("Place Autocomplete API error"),
    );
    throw new Error(getGoogleApiErrorMessage(errorData));
  }

  const data = await response.json();

  if (data.suggestions) {
    return data.suggestions.map((suggestion: any) => ({
      place_id: suggestion.placePrediction.placeId,
      description: suggestion.placePrediction.text.text,
      structured_formatting: {
        main_text:
          suggestion.placePrediction.structuredFormat?.mainText?.text || "",
        secondary_text:
          suggestion.placePrediction.structuredFormat?.secondaryText?.text ||
          "",
      },
    }));
  }
  return [];
};

/**
 * Fetches detailed information for a specific place using the Places API (New) REST endpoint.
 * @param placeId The Place ID from an autocomplete prediction.
 * @returns A promise that resolves with the GeocodeResult.
 */
export const getPlaceDetails = async (
  placeId: string,
): Promise<GeocodeResult> => {
  const { GOOGLE_API_KEY } = await getApiKeys();
  if (!GOOGLE_API_KEY) {
    throw new Error(
      "API Key not found. Please set the `API_KEY` environment variable.",
    );
  }

  const url = `https://places.googleapis.com/v1/places/${placeId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": GOOGLE_API_KEY,
      "X-Goog-FieldMask": "formattedAddress,location",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    crashlytics.logError(
      `Error fetching place details: ${JSON.stringify(errorData)}`,
      "locationService.ts",
      new Error("Place Details API error"),
    );
    throw new Error(getGoogleApiErrorMessage(errorData));
  }

  const place = await response.json();

  if (place && place.location) {
    return {
      isValid: true,
      formattedAddress: place.formattedAddress,
      coordinates: {
        lat: place.location.latitude,
        lng: place.location.longitude,
      },
    };
  } else {
    console.warn(`Place Details failed for "${placeId}". Response:`, place);
    throw new Error("Could not retrieve details for that location.");
  }
};

/**
 * Validates a location name using the Google Geocoding API (New) REST endpoint.
 * @param locationName The name of the location to validate.
 * @returns A promise that resolves with the validation result.
 */
// Common Singapore locations fallback
const SINGAPORE_LOCATIONS_FALLBACK: Record<
  string,
  { lat: number; lng: number; address: string }
> = {
  "marina bay sands": {
    lat: 1.2834,
    lng: 103.8607,
    address: "Marina Bay Sands, Singapore",
  },
  sentosa: { lat: 1.2494, lng: 103.8303, address: "Sentosa, Singapore" },
  "clarke quay": {
    lat: 1.2884,
    lng: 103.847,
    address: "Clarke Quay, Singapore",
  },
  "orchard road": {
    lat: 1.3048,
    lng: 103.8318,
    address: "Orchard Road, Singapore",
  },
  "east coast park": {
    lat: 1.3016,
    lng: 103.9056,
    address: "East Coast Park, Singapore",
  },
  "gardens by the bay": {
    lat: 1.2816,
    lng: 103.8636,
    address: "Gardens by the Bay, Singapore",
  },
  "changi airport": {
    lat: 1.3644,
    lng: 103.9915,
    address: "Singapore Changi Airport",
  },
  "little india": {
    lat: 1.3067,
    lng: 103.8524,
    address: "Little India, Singapore",
  },
  chinatown: { lat: 1.2831, lng: 103.8448, address: "Chinatown, Singapore" },
  harbourfront: {
    lat: 1.2653,
    lng: 103.8215,
    address: "HarbourFront, Singapore",
  },
};

export const geocodeByAddress = async (
  locationName: string,
): Promise<GeocodeResult> => {
  // First try common locations fallback
  const lowerLocation = locationName.toLowerCase();
  for (const [key, value] of Object.entries(SINGAPORE_LOCATIONS_FALLBACK)) {
    if (lowerLocation.includes(key)) {
      return {
        isValid: true,
        formattedAddress: value.address,
        coordinates: { lat: value.lat, lng: value.lng },
      };
    }
  }

  return new Promise((resolve, reject) => {
    // Wait for Google Maps to load
    const checkGoogleMaps = () => {
      if (
        typeof (window as any).google !== "undefined" &&
        (window as any).google.maps
      ) {
        const geocoder = new (window as any).google.maps.Geocoder();

        geocoder.geocode(
          {
            address: `${locationName}, Singapore`,
            region: "SG",
          },
          (results: any[], status: string) => {
            if (status === "OK" && results && results.length > 0) {
              const result = results[0];
              resolve({
                isValid: true,
                formattedAddress: result.formatted_address,
                coordinates: {
                  lat: result.geometry.location.lat(),
                  lng: result.geometry.location.lng(),
                },
              });
            } else {
              console.warn(
                `Geocoding failed for "${locationName}". Status:`,
                status,
              );
              resolve({ isValid: false });
            }
          },
        );
      } else {
        // If Google Maps isn't loaded yet, wait and try again
        setTimeout(checkGoogleMaps, 100);
      }
    };

    checkGoogleMaps();

    // Timeout after 10 seconds
    setTimeout(() => {
      console.warn(
        `Geocoding timeout for "${locationName}" - Google Maps API not available`,
      );
      resolve({ isValid: false });
    }, 10000);
  });
};
